/**
 * Document layout template types (Rechnungsvorlage).
 * Coordinates are in PDF points (pt) so the canvas and @react-pdf/renderer agree.
 * A4 = 595.28 × 841.89 pt.
 */

export const PDF_A4_WIDTH_PT = 595.28;
export const PDF_A4_HEIGHT_PT = 841.89;

/** 1 mm in PDF points (72 pt/inch, 25.4 mm/inch). */
export const PDF_MM_TO_PT = 72 / 25.4;

export function mmToPt(mm: number): number {
  return mm * PDF_MM_TO_PT;
}

export type DocumentPageMarginsPt = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

/** Existing PDF geometry, used as fallback for layouts saved before page margins existed. */
export const DEFAULT_DOCUMENT_PAGE_MARGINS_PT: Readonly<DocumentPageMarginsPt> = {
  top: 36,
  right: 40,
  bottom: 48,
  left: 40,
};

/**
 * DIN-5008-oriented writing area. Form B's 45 mm letterhead remains controlled
 * by the header band; 20 mm top/bottom are practical continuation-page margins.
 */
export const DIN5008_RECOMMENDED_PAGE_MARGINS_PT: Readonly<DocumentPageMarginsPt> = {
  top: mmToPt(20),
  right: mmToPt(20),
  bottom: mmToPt(20),
  left: mmToPt(25),
};

export function resolveDocumentPageMargins(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): DocumentPageMarginsPt {
  const stored = layout?.body.pageMarginsPt;
  return {
    top: stored?.top ?? DEFAULT_DOCUMENT_PAGE_MARGINS_PT.top,
    right: stored?.right ?? DEFAULT_DOCUMENT_PAGE_MARGINS_PT.right,
    bottom: stored?.bottom ?? DEFAULT_DOCUMENT_PAGE_MARGINS_PT.bottom,
    left: stored?.left ?? DEFAULT_DOCUMENT_PAGE_MARGINS_PT.left,
  };
}

/**
 * DIN 5008 Form B — Empfänger-Anschriftzone im Fensterbrief.
 * Text beginnt 25 mm von links; Zone ab 62,7 mm von oben; ~85×27,3 mm Feld.
 * Form B is the common window-envelope layout with a taller letterhead.
 */
export const DIN5008_FORM_B_ADDRESS_WINDOW = {
  /** 25 mm from left (text flush with body). */
  xPt: Math.round(mmToPt(25) * 10) / 10,
  /** Form B Empfängerzone starts ~62.7 mm from top. */
  yPt: Math.round(mmToPt(62.7) * 10) / 10,
  /** ~80 mm usable width inside the 85 mm window (5 mm inset). */
  wPt: Math.round(mmToPt(80) * 10) / 10,
  /** Empfänger-Anschriftzone ~27.3 mm (fits typical 3–4 address lines). */
  hPt: Math.round(mmToPt(27.3) * 10) / 10,
} as const;

/** Header-band elements (page 1, absolutely positioned). */
export const HEADER_ELEMENT_KEYS = [
  'logo',
  'senderHeadline',
  'recipient',
  'ansprechpartner',
  'documentTitle',
  'documentType',
  'offerTitle',
  'invoiceNumber',
  'issueDate',
  'dueDate',
  'customerNumber',
  'projectNumber',
  'orderNumber',
  'leistungsort',
  'leistungszeitraum',
  'headerBanner',
  'companyAddress',
  'companyPhone',
  'companyEmail',
  'companyWebsite',
  'freeText',
  'divider',
  'colorBlock',
] as const;

/** Footer-band elements (all pages, fixed). */
export const FOOTER_ELEMENT_KEYS = [
  'legalBlock',
  'bankBlock',
  'contactBlock',
  'pageNumber',
  'signatureImage',
  'invoiceNumber',
  'divider',
  'colorBlock',
  'freeText',
  'logo',
  'headerBanner',
] as const;

export type HeaderElementKey = (typeof HEADER_ELEMENT_KEYS)[number];
export type FooterElementKey = (typeof FOOTER_ELEMENT_KEYS)[number];
export type LayoutElementKey = HeaderElementKey | FooterElementKey;

export type LayoutBand = 'header' | 'footer';

export type LayoutTextAlign = 'left' | 'center' | 'right';

/** Label above value (default) vs "Label: Wert" on one line. */
export type LayoutLabelPlacement = 'stacked' | 'inline';

/** Missing / unknown = horizontal (legacy Trennlinie). */
export type LayoutLineOrientation = 'horizontal' | 'vertical';

/** Hairline floor for divider thickness (horizontal h / vertical w). */
export const LAYOUT_LINE_MIN_THICKNESS_PT = 0.25;

/** Header fields that show a label + dynamic value. */
export const LABELED_FIELD_KEYS = [
  'documentTitle',
  'invoiceNumber',
  'issueDate',
  'dueDate',
  'customerNumber',
  'projectNumber',
  'orderNumber',
  'leistungsort',
  'leistungszeitraum',
] as const;

export type LabeledFieldKey = (typeof LABELED_FIELD_KEYS)[number];

export function isLabeledFieldKey(key: LayoutElementKey): key is LabeledFieldKey {
  return (LABELED_FIELD_KEYS as readonly string[]).includes(key);
}

export type LayoutElement = {
  id: string;
  key: LayoutElementKey;
  xPt: number;
  yPt: number;
  wPt: number;
  hPt: number;
  align?: LayoutTextAlign;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontSizePt?: number;
  color?: string;
  bg?: string;
  radiusPt?: number;
  border?: boolean;
  z?: number;
  locked?: boolean;
  /** Free-text / note content. */
  text?: string;
  /** Storage path for banner / signature images (app-files bucket). */
  imageObjectPath?: string;
  /** Bild (headerBanner): missing = full color. */
  imageGrayscale?: boolean;
  /** divider only; missing = horizontal. */
  lineOrientation?: LayoutLineOrientation;
  /** stacked (default) = label above value; inline = "Label: Wert". */
  labelPlacement?: LayoutLabelPlacement;
  /** Optional label override (empty string hides the label). */
  labelText?: string;
  labelBold?: boolean;
  labelColor?: string;
  labelFontSizePt?: number;
};

/** Where JPEG/PNG offer attachments render when a custom layout is active. */
export type DocumentLayoutImagePlacement = 'appendix' | 'firstPageGrid';

/**
 * Where the position table starts. Missing / flow = continues after intro.
 * auto = like flow, but starts on a new page when the current one has no room for the table
 * header plus the first TABLE_START_MIN_POSITIONS positions (no orphaned table start).
 * page2 = never before page 2.
 */
export type DocumentLayoutTablePlacement = 'flow' | 'auto' | 'page2';

/** „Automatisch“: so viele Positionen müssen mit dem Tabellenkopf auf dieselbe Seite passen. */
export const TABLE_START_MIN_POSITIONS = 3;

/** Where the GiroCode sits when paymentQr is on. Missing / totals = next to amounts. */
export type DocumentLayoutPaymentQrPlacement = 'totals' | 'afterGreeting';

/** Horizontal alignment when paymentQrPlacement is afterGreeting. Missing / left = current default. */
export type DocumentLayoutPaymentQrAlign = 'left' | 'center';

export type DocumentLayoutV1 = {
  v: 1;
  header: { heightPt: number; elements: LayoutElement[] };
  footer: { heightPt: number; elements: LayoutElement[] };
  body: {
    accentColor: string;
    tableHeaderBg: string;
    fontSizePt: number;
    /** Safe writing-area margins. Header/footer bands may intentionally extend beyond them. */
    pageMarginsPt?: DocumentPageMarginsPt;
    /**
     * When true, print DIN 5008 Form B Falz- and Lochmarken (Falthilfe) in the left Heftrand.
     * Missing / false = off (legacy templates unchanged).
     */
    foldMarks?: boolean;
    /**
     * When true, print Übertrag (running net) and repeat the position table header across pages.
     * Missing / false = off (default template and legacy layouts unchanged).
     */
    pageCarryOver?: boolean;
    /**
     * When true, print a Zusammenstellung of all title-group nets above Netto/USt/Brutto.
     * Missing / false = off (default template and legacy layouts unchanged).
     */
    titleTotalsSummary?: boolean;
    /**
     * When true, print an EPC GiroCode (payment QR) on invoice-family PDFs.
     * Missing / false = off (default template and legacy layouts unchanged).
     */
    paymentQr?: boolean;
    /**
     * Where to print the GiroCode when paymentQr is true.
     * Missing / totals = next to Netto/USt/Brutto (legacy).
     */
    paymentQrPlacement?: DocumentLayoutPaymentQrPlacement;
    /**
     * Left/center when paymentQrPlacement is afterGreeting.
     * Missing / left = legacy left alignment.
     */
    paymentQrAlign?: DocumentLayoutPaymentQrAlign;
    /** Missing / appendix = images become full A4 pages after the PDF. */
    imagePlacement?: DocumentLayoutImagePlacement;
    /** Missing / flow = table follows intro on the same page. New templates default to auto. */
    tablePlacement?: DocumentLayoutTablePlacement;
  };
};

/** @deprecated Inline images are placed in the document body; attachments always stay in the appendix. */
export function layoutPlacesAttachmentImagesOnFirstPage(
  _layout?: Pick<DocumentLayoutV1, 'body'> | null,
): boolean {
  return false;
}

export function layoutStartsTableOnPage2(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): boolean {
  return layout?.body.tablePlacement === 'page2';
}

export function layoutTablePlacement(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): DocumentLayoutTablePlacement {
  const value = layout?.body.tablePlacement;
  return value === 'page2' || value === 'auto' ? value : 'flow';
}

export function layoutShowsPageCarryOver(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): boolean {
  return layout?.body.pageCarryOver === true;
}

export function layoutShowsTitleTotalsSummary(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): boolean {
  return layout?.body.titleTotalsSummary === true;
}

export function layoutShowsPaymentQr(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): boolean {
  return layout?.body.paymentQr === true;
}

export function layoutPaymentQrPlacement(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): DocumentLayoutPaymentQrPlacement {
  return layout?.body.paymentQrPlacement === 'afterGreeting' ? 'afterGreeting' : 'totals';
}

export function layoutPaymentQrAlign(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): DocumentLayoutPaymentQrAlign {
  return layout?.body.paymentQrAlign === 'center' ? 'center' : 'left';
}

/** Drop legacy free-placed GiroCode elements (feature moved to body.paymentQr toggle). */
export function stripLegacyPaymentQrElements(layout: DocumentLayoutV1): DocumentLayoutV1 {
  const next = structuredClone(layout);
  next.header.elements = next.header.elements.filter((e) => (e.key as string) !== 'paymentQr');
  next.footer.elements = next.footer.elements.filter((e) => (e.key as string) !== 'paymentQr');
  return next;
}

export type DocumentLayoutTemplateRow = {
  id: string;
  instance_id: string;
  name: string;
  is_default: boolean;
  version: number;
  layout: DocumentLayoutV1;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};

/** §14 UStG / GoBD mandatory elements that must be present in a valid template. */
export const MANDATORY_HEADER_KEYS: readonly HeaderElementKey[] = [
  'senderHeadline',
  'recipient',
  'documentTitle',
  'issueDate',
  'leistungszeitraum',
] as const;

export const MANDATORY_FOOTER_KEYS: readonly FooterElementKey[] = [
  'legalBlock',
  'bankBlock',
] as const;

export type LayoutFieldDef = {
  key: LayoutElementKey;
  label: string;
  band: LayoutBand;
  required: boolean;
  kind: 'field' | 'design';
  defaultSize: { wPt: number; hPt: number };
};

export const LAYOUT_FIELD_DEFS: LayoutFieldDef[] = [
  { key: 'senderHeadline', label: 'Absenderzeile', band: 'header', required: true, kind: 'field', defaultSize: { wPt: 320, hPt: 16 } },
  { key: 'recipient', label: 'Kundenname/Adresse', band: 'header', required: true, kind: 'field', defaultSize: { wPt: 260, hPt: 56 } },
  { key: 'ansprechpartner', label: 'Ansprechpartner', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 220, hPt: 56 } },
  { key: 'documentTitle', label: 'Rechnung / Nummer', band: 'header', required: true, kind: 'field', defaultSize: { wPt: 360, hPt: 22 } },
  { key: 'documentType', label: 'Dokumententyp', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 140, hPt: 22 } },
  { key: 'offerTitle', label: 'Betreff', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 360, hPt: 22 } },
  { key: 'invoiceNumber', label: 'Dokumentnummer', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 180, hPt: 32 } },
  { key: 'issueDate', label: 'Ausstellungsdatum', band: 'header', required: true, kind: 'field', defaultSize: { wPt: 140, hPt: 36 } },
  { key: 'dueDate', label: 'Fälligkeitsdatum', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 140, hPt: 36 } },
  { key: 'customerNumber', label: 'Kundennummer', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 140, hPt: 36 } },
  { key: 'projectNumber', label: 'Projektnummer', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 140, hPt: 36 } },
  { key: 'orderNumber', label: 'Bestellnummer', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 140, hPt: 36 } },
  { key: 'leistungsort', label: 'Leistungsort', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 200, hPt: 36 } },
  { key: 'leistungszeitraum', label: 'Leistungszeitraum', band: 'header', required: true, kind: 'field', defaultSize: { wPt: 160, hPt: 36 } },
  { key: 'logo', label: 'Firmenlogo', band: 'header', required: false, kind: 'design', defaultSize: { wPt: 160, hPt: 56 } },
  { key: 'headerBanner', label: 'Bild', band: 'header', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 80 } },
  { key: 'companyAddress', label: 'Firmenadresse', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 180, hPt: 28 } },
  { key: 'companyPhone', label: 'Telefon', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 160, hPt: 16 } },
  { key: 'companyEmail', label: 'E-Mail', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 180, hPt: 16 } },
  { key: 'companyWebsite', label: 'Website', band: 'header', required: false, kind: 'field', defaultSize: { wPt: 180, hPt: 16 } },
  { key: 'freeText', label: 'Freitext', band: 'header', required: false, kind: 'design', defaultSize: { wPt: 240, hPt: 40 } },
  { key: 'divider', label: 'Trennlinie', band: 'header', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 2 } },
  { key: 'colorBlock', label: 'Hintergrundfläche', band: 'header', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 48 } },
  { key: 'legalBlock', label: 'Rechtliches (USt-ID / Register)', band: 'footer', required: true, kind: 'field', defaultSize: { wPt: 170, hPt: 58 } },
  { key: 'bankBlock', label: 'IBAN/Bankdaten', band: 'footer', required: true, kind: 'field', defaultSize: { wPt: 170, hPt: 58 } },
  { key: 'contactBlock', label: 'Kontaktdaten', band: 'footer', required: false, kind: 'field', defaultSize: { wPt: 170, hPt: 58 } },
  { key: 'pageNumber', label: 'Seitenzahl', band: 'footer', required: false, kind: 'field', defaultSize: { wPt: 90, hPt: 14 } },
  { key: 'invoiceNumber', label: 'Dokumentnummer', band: 'footer', required: false, kind: 'field', defaultSize: { wPt: 180, hPt: 32 } },
  { key: 'signatureImage', label: 'Unterschrift', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 140, hPt: 48 } },
  { key: 'freeText', label: 'Freitext', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 200, hPt: 32 } },
  { key: 'divider', label: 'Trennlinie', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 2 } },
  { key: 'colorBlock', label: 'Hintergrundfläche', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 36 } },
  { key: 'logo', label: 'Firmenlogo', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 110, hPt: 40 } },
  { key: 'headerBanner', label: 'Bild', band: 'footer', required: false, kind: 'design', defaultSize: { wPt: 515, hPt: 48 } },
];

export function isDocumentLayoutV1(x: unknown): x is DocumentLayoutV1 {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (o.v !== 1) return false;
  if (typeof o.header !== 'object' || o.header === null) return false;
  if (typeof o.footer !== 'object' || o.footer === null) return false;
  if (typeof o.body !== 'object' || o.body === null) return false;
  const header = o.header as Record<string, unknown>;
  const footer = o.footer as Record<string, unknown>;
  return Array.isArray(header.elements) && Array.isArray(footer.elements);
}

export function newLayoutElementId(key: string): string {
  return `${key}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function resolveLineOrientation(
  el: Pick<LayoutElement, 'lineOrientation'>,
): LayoutLineOrientation {
  return el.lineOrientation === 'vertical' ? 'vertical' : 'horizontal';
}

export function lineThicknessPt(
  el: Pick<LayoutElement, 'wPt' | 'hPt' | 'lineOrientation'>,
): number {
  const raw = resolveLineOrientation(el) === 'vertical' ? el.wPt : el.hPt;
  return Math.max(LAYOUT_LINE_MIN_THICKNESS_PT, raw);
}

/** Smallest geometry the validator accepts — hairlines for lines, 1–2 pt otherwise. */
export function minElementWidthPt(key: LayoutElementKey): number {
  if (key === 'divider') return LAYOUT_LINE_MIN_THICKNESS_PT;
  if (key === 'headerBanner') return 1;
  return 2;
}

export function minElementHeightPt(key: LayoutElementKey): number {
  return key === 'divider' ? LAYOUT_LINE_MIN_THICKNESS_PT : 1;
}

export function isCompanyContactFieldKey(
  key: LayoutElementKey,
): key is 'companyAddress' | 'companyPhone' | 'companyEmail' | 'companyWebsite' {
  return (
    key === 'companyAddress' ||
    key === 'companyPhone' ||
    key === 'companyEmail' ||
    key === 'companyWebsite'
  );
}
